'use client'

import React, { useState, useMemo } from 'react'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { motion } from 'framer-motion'
import LinkWithIcon from '@/components/ui/LinkWithIcon'
import SimpleProjectCard from '@/components/SimpleProjectCard'
import SectionHeader from '@/components/ui/SectionHeader'
import { SectionWrapper } from '@/components/SectionWrapper'
import MotionFadeIn from '@/components/ui/MotionFadeIn'
import { normalizeSectionSettings } from '@/utilities/sectionSettings'
import type { FilteredProjectsBlock, Project } from '@/payload-types'

// Filter button component
interface FilterButtonProps {
  label: string
  isActive: boolean
  onClick: () => void
}

function FilterButton({ label, isActive, onClick }: FilterButtonProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'px-3 py-2 font-ui transition-all duration-200 uppercase',
        isActive
          ? 'bg-brand text-text-white border border-border-brand'
          : 'text-text-brand border border-border-brand hover:bg-brand hover:text-text-white',
      )}
    >
      {label}
    </button>
  )
}

interface FilteredProjectOverviewSectionProps {
  projects: Project[]
  products: string[]
  header: FilteredProjectsBlock['header']
  showAllLink?: FilteredProjectsBlock['showAllLink']
  allProjectsLinkText?: FilteredProjectsBlock['allProjectsLinkText']
  allProjectsLinkUrl?: FilteredProjectsBlock['allProjectsLinkUrl']
  settings?: FilteredProjectsBlock['settings']
}

export default function FilteredProjectOverviewSection({
  projects,
  products,
  header,
  showAllLink = true,
  allProjectsLinkText = 'Alle Projekte anzeigen',
  allProjectsLinkUrl = '/projekte',
  settings,
}: FilteredProjectOverviewSectionProps) {
  const normalizedSettings = normalizeSectionSettings(settings)

  // Normalize header (convert null to undefined, provide defaults)
  const normalizedHeader = {
    infoTagText: header?.infoTagText ?? undefined,
    title: header?.title || 'Unsere Projekte',
    subtitle: header?.subtitle ?? undefined,
  }
  const [activeFilter, setActiveFilter] = useState('Alle')

  // Add "Alle" to topics if not present
  const allProducts = products.includes('Alle') ? products : ['Alle', ...products]

  // Filter projects based on active filter (memoized for performance)
  const filteredProjects = useMemo(() => {
    return activeFilter === 'Alle'
      ? projects
      : projects.filter((project) => {
          // Check if project has the selected product
          const hasProduct = (project.products || []).some(
            (product) => (typeof product === 'object' ? product.title : product) === activeFilter,
          )

          // Fallback: if no products assigned, check if project title contains the filter term
          const hasTitleMatch =
            !project.products?.length &&
            project.title.toLowerCase().includes(activeFilter.toLowerCase())

          return hasProduct || hasTitleMatch
        })
  }, [activeFilter, projects])

  return (
    <SectionWrapper settings={normalizedSettings}>
      {/* Header */}
      <MotionFadeIn>
        <SectionHeader
          infoTagText={normalizedHeader.infoTagText || ''}
          title={normalizedHeader.title}
          subtitle={normalizedHeader.subtitle}
          titleClassName="uppercase"
          subtitleClassName="text-text-secondary"
        />
      </MotionFadeIn>

      {/* Filters */}
      <MotionFadeIn>
        <div className="mb-8">
          {/* Desktop: button row */}
          <div className="hidden lg:flex flex-wrap gap-4">
            {allProducts.map((topic) => (
              <FilterButton
                key={topic}
                label={topic}
                isActive={activeFilter === topic}
                onClick={() => setActiveFilter(topic)}
              />
            ))}
          </div>

          {/* Mobile & Tablet: native select */}
          <div className="lg:hidden relative">
            <label htmlFor="filtered-projects-filter" className="sr-only">
              Kategorie filtern
            </label>
            <select
              id="filtered-projects-filter"
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
              className="w-full appearance-none bg-transparent border-b border-text-primary/30 py-3 pr-10 pl-0 font-ui text-base uppercase text-text-primary focus:outline-none focus:border-text-primary transition-colors"
            >
              {allProducts.map((topic) => (
                <option key={topic} value={topic}>
                  {topic}
                </option>
              ))}
            </select>
            <ChevronRight
              aria-hidden="true"
              className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 h-5 w-5 rotate-90 text-text-primary/60"
            />
          </div>
        </div>
      </MotionFadeIn>

      {/* Projects Grid */}
      <div className="mb-8">
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 justify-items-center items-center"
          layout
        >
          {filteredProjects.map((project) => (
            <motion.div
              key={project.id}
              layout
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="w-full min-w-[280px] max-w-[400px]"
            >
              <MotionFadeIn>
                <SimpleProjectCard project={project} sourceBlock="filteredProjects" />
              </MotionFadeIn>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Link to all projects */}
      {showAllLink && (
        <MotionFadeIn>
          <div className="">
            <LinkWithIcon
              href={allProjectsLinkUrl || '/projekte'}
              text={allProjectsLinkText || 'Alle Projekte anzeigen'}
              className=""
              variant="link"
              color="brand"
              size="md"
            />
          </div>
        </MotionFadeIn>
      )}
    </SectionWrapper>
  )
}
